import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  categories,
  formatPrice,
  siteConfig,
} from "../Datas/productData";
import "../css/shop.scss";

function Shop() {
  const { addToCart, toggleWishlist, wishlistItems } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [addedProductId, setAddedProductId] = useState(null);

  const category = searchParams.get("category") || "All";
  const query = searchParams.get("q") || "";
  const wishlistOnly = searchParams.get("view") === "wishlist";
  const sortParam = searchParams.get("sort") || "featured";

  useEffect(() => {
    setSortBy(sortParam);
  }, [sortParam]);

  useEffect(() => {
    if (!addedProductId) return undefined;
    const timer = window.setTimeout(() => setAddedProductId(null), 1400);
    return () => window.clearTimeout(timer);
  }, [addedProductId]);

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const matchesCategory = category === "All" || product.category === category;
      const searchableText = [
        product.name,
        product.category,
        product.description,
        ...product.tags,
        ...product.colors,
      ]
        .join(" ")
        .toLowerCase();
      const matchesQuery = !normalizedQuery || searchableText.includes(normalizedQuery);
      const matchesWishlist = !wishlistOnly || wishlistItems.includes(product.id);
      return matchesCategory && matchesQuery && matchesWishlist;
    });

    return [...filtered].sort((firstProduct, secondProduct) => {
      switch (sortBy) {
        case "price-low":
          return firstProduct.price - secondProduct.price;
        case "price-high":
          return secondProduct.price - firstProduct.price;
        case "name":
          return firstProduct.name.localeCompare(secondProduct.name);
        case "rating":
          return secondProduct.rating - firstProduct.rating;
        default:
          return Number(secondProduct.featured) - Number(firstProduct.featured);
      }
    });
  }, [category, query, sortBy, wishlistItems, wishlistOnly]);

  const updateParam = (name, value, defaultValue = "") => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) nextParams.delete(name);
    else nextParams.set(name, value);
    setSearchParams(nextParams);
  };

  const handleSort = (event) => {
    const nextSort = event.target.value;
    setSortBy(nextSort);
    updateParam("sort", nextSort, "featured");
  };

  const clearFilters = () => {
    setSortBy("featured");
    setSearchParams({});
  };

  const quickAdd = (product) => {
    addToCart(product.id, 1, product.sizes[0], product.colors[0]);
    setAddedProductId(product.id);
  };

  const pageTitle = wishlistOnly
    ? "Saved pieces"
    : query
      ? `Results for “${query}”`
      : category === "All"
        ? "All things"
        : category;

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="shop" aria-labelledby="shop-heading">
      <section className="shop__intro page-shell">
        <p className="eyebrow">The collection</p>
        <h1 id="shop-heading">{pageTitle}</h1>
        <p>
          Useful objects, soft shapes, and thoughtful details made to settle into
          daily life.
        </p>
      </section>

      <div className="shop__category-bar" aria-label="Product categories">
        <div className="shop__category-scroll page-shell">
          {categories.map((item) => (
            <button
              type="button"
              key={item}
              className={category === item && !wishlistOnly ? "is-active" : ""}
              aria-pressed={category === item && !wishlistOnly}
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete("view");
                if (item === "All") nextParams.delete("category");
                else nextParams.set("category", item);
                setSearchParams(nextParams);
              }}
            >
              {item}
            </button>
          ))}
          <button
            type="button"
            className={wishlistOnly ? "is-active" : ""}
            aria-pressed={wishlistOnly}
            onClick={() => updateParam("view", wishlistOnly ? "" : "wishlist")}
          >
            Saved ({wishlistItems.length})
          </button>
        </div>
      </div>

      <section className="shop__catalog page-shell">
        <div className="shop__toolbar">
          <p aria-live="polite" aria-atomic="true">
            {visibleProducts.length} pieces
          </p>
          <div className="shop__toolbar-actions">
            <button
              type="button"
              className="shop__filter-toggle"
              onClick={() => setFiltersOpen((current) => !current)}
              aria-expanded={filtersOpen}
              aria-controls="shop-filter-panel"
            >
              Filter & sort <span aria-hidden="true">{filtersOpen ? "−" : "+"}</span>
            </button>
            <label className="shop__sort">
              <span>Sort by</span>
              <select value={sortBy} onChange={handleSort}>
                <option value="featured">Featured</option>
                <option value="rating">Top rated</option>
                <option value="price-low">Price, low to high</option>
                <option value="price-high">Price, high to low</option>
                <option value="name">Name, A–Z</option>
              </select>
            </label>
          </div>
        </div>

        {filtersOpen && (
          <div className="shop__filter-panel" id="shop-filter-panel">
            <div>
              <span className="eyebrow">Category</span>
              <div className="shop__filter-options">
                {categories.map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={category === item ? "is-active" : ""}
                    aria-pressed={category === item}
                    onClick={() => updateParam("category", item, "All")}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="eyebrow">Current selection</span>
              <p>
                {query && <span>Search: {query}</span>}
                {category !== "All" && <span>Category: {category}</span>}
                {wishlistOnly && <span>Saved only</span>}
              </p>
            </div>
            <button type="button" className="text-link" onClick={clearFilters}>
              Clear all
            </button>
          </div>
        )}

        {visibleProducts.length > 0 ? (
          <div className="shop__grid">
            {visibleProducts.map((product, index) => {
              const isWishlisted = wishlistItems.includes(product.id);
              return (
                <article className="shop-card" key={product.id}>
                  <div className="shop-card__media">
                    <Link
                      to={`/shopDetails/${product.id}`}
                      onClick={scrollToTop}
                      aria-label={`View ${product.name}`}
                    >
                      <img
                        className="shop-card__primary"
                        src={product.images[0]}
                        alt={product.name}
                        width="1120"
                        height="1400"
                        loading={index < 8 ? "eager" : "lazy"}
                      />
                      <img
                        className="shop-card__secondary"
                        src={product.images[1]}
                        alt=""
                        width="1120"
                        height="1400"
                        loading="lazy"
                      />
                    </Link>
                    {product.badge && <span className="shop-card__badge">{product.badge}</span>}
                    <button
                      type="button"
                      className={`shop-card__wishlist${isWishlisted ? " is-active" : ""}`}
                      onClick={() => toggleWishlist(product.id)}
                      aria-pressed={isWishlisted}
                      aria-label={`${isWishlisted ? "Remove" : "Save"} ${product.name}`}
                    >
                      <span aria-hidden="true">{isWishlisted ? "♥" : "♡"}</span>
                    </button>
                    <button
                      type="button"
                      className="shop-card__quick-add"
                      onClick={() => quickAdd(product)}
                    >
                      {addedProductId === product.id ? "Added to bag" : "Quick add"}
                    </button>
                  </div>
                  <div className="shop-card__info">
                    <div>
                      <p className="shop-card__category">{product.category}</p>
                      <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                        <h2>{product.name}</h2>
                      </Link>
                    </div>
                    <p className="shop-card__price">{formatPrice(product.price)}</p>
                  </div>
                  <p className="shop-card__colors">{product.colors.join(" · ")}</p>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="shop__empty">
            <p className="eyebrow">Nothing here just yet</p>
            <h2>Let’s try a wider search.</h2>
            <p>
              {wishlistOnly
                ? "Save a few favourites and they’ll wait for you here."
                : "Try a different word or clear the selected category."}
            </p>
            <button type="button" className="button button--dark" onClick={clearFilters}>
              View all products
            </button>
          </div>
        )}

        <aside className="shop__service-note">
          <span>Designed with intention</span>
          <p>{siteConfig.description}</p>
          <Link to="/about" onClick={scrollToTop}>Read our story <span aria-hidden="true">↗</span></Link>
        </aside>
      </section>
    </main>
  );
}

export default Shop;
